import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Check, X, Zap, Calculator, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Pricing() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, authToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly'); // 'monthly' or 'yearly'
  const [plans, setPlans] = useState(null);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    // Fetch plans from API
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/plans`);
        setPlans(response.data);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
        toast.error('Failed to load pricing plans');
      }
    };
    fetchPlans();
  }, []);

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user already has this plan
    if (user?.plan === planId) {
      return;
    }

    // Downgrade to free - cancel subscription
    if (planId === 'free') {
      navigate('/settings');
      toast.info(t('pricing.downgradeFree'));
      return;
    }

    setLoading(planId);
    try {
      const response = await axios.post(
        `${API}/subscriptions/create`,
        {
          plan: planId,
          origin_url: window.location.origin,
          upgrade_timing: 'immediate' // For now, always immediate
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('pricing.upgradeFailed'));
      setLoading(null);
    }
  };

  if (!plans) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const getPlanDisplay = (tier) => {
    if (tier === 'free') return plans.free;
    return billingInterval === 'monthly' ? plans[tier].monthly : plans[tier].yearly;
  };

  const isCurrentPlan = (planId) => {
    return user?.plan === planId;
  };

  const getButtonText = (planId) => {
    if (!isAuthenticated) return t('pricing.getStarted');
    if (planId === 'free') {
      return isCurrentPlan('free') ? t('pricing.currentPlan') : t('pricing.downgrade');
    }
    if (isCurrentPlan(planId)) return t('pricing.currentPlan');
    
    // Check if it's an upgrade or downgrade
    const currentTier = user?.plan?.includes('basic') ? 'basic' : user?.plan?.includes('pro') ? 'pro' : 'free';
    const targetTier = planId.includes('basic') ? 'basic' : 'pro';
    
    if (currentTier === 'free') return t('pricing.upgrade');
    if (currentTier === 'basic' && targetTier === 'pro') return t('pricing.upgrade');
    if (currentTier === 'pro' && targetTier === 'basic') return t('pricing.downgrade');
    
    return t('pricing.changePlan');
  };

  const renderPlanCard = (tier, isPopular = false) => {
    const plan = getPlanDisplay(tier);
    const planId = plan.name;
    
    return (
      <div
        key={planId}
        className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${
          isPopular ? 'ring-2 ring-emerald-500 transform scale-105' : ''
        }`}
      >
        {isPopular && (
          <div className="absolute top-0 right-0 bg-emerald-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
            {t('pricing.popular')}
          </div>
        )}

        <div className="p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {t(`pricing.plans.${tier}.name`)}
          </h3>
          <p className="text-gray-600 mb-6">
            {t(`pricing.plans.${tier}.description`)}
          </p>

          <div className="mb-6">
            <span className="text-5xl font-bold text-gray-900">
              €{plan.price}
            </span>
            {plan.interval && (
              <span className="text-gray-600 ml-2">
                / {plan.interval === 'month' ? t('pricing.perMonth') : t('pricing.perYear')}
              </span>
            )}
          </div>

          <button
            onClick={() => handleUpgrade(planId)}
            disabled={loading || isCurrentPlan(planId)}
            className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
              isCurrentPlan(planId)
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                : isPopular
                ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg hover:shadow-xl'
                : 'bg-gray-900 text-white hover:bg-gray-800'
            } ${loading === planId ? 'opacity-50 cursor-wait' : ''}`}
          >
            {loading === planId ? t('common.loading') : getButtonText(planId)}
          </button>

          <ul className="mt-8 space-y-4">
            {Object.entries(plan.features).map(([key, value]) => {
              const included = value === true || (typeof value === 'string' && value !== 'false');
              const displayValue = typeof value === 'string' ? value : null;
              
              return (
                <li key={key} className="flex items-start">
                  {included ? (
                    <Check className="h-5 w-5 text-emerald-500 mr-3 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="h-5 w-5 text-gray-300 mr-3 flex-shrink-0 mt-0.5" />
                  )}
                  <span className={included ? 'text-gray-700' : 'text-gray-400'}>
                    {t(`pricing.features.${key}`, { value: displayValue })}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Language Switcher */}
      <div className="max-w-7xl mx-auto flex justify-end mb-4">
        <Select value={i18n.language} onValueChange={changeLanguage}>
          <SelectTrigger className="w-24 h-9">
            <Globe className="h-4 w-4 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="de">DE</SelectItem>
            <SelectItem value="en">EN</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Header */}
      <div className="max-w-7xl mx-auto text-center mb-12">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="inline-flex items-center space-x-2 text-emerald-600 hover:text-emerald-700">
            <Calculator className="h-8 w-8" />
            <span className="text-2xl font-bold">ivoliq</span>
          </Link>
          {isAuthenticated && (
            <Link 
              to="/"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← {t('common.back')}
            </Link>
          )}
        </div>
        
        <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('pricing.title')}</h1>
        <p className="text-xl text-gray-600 mb-8">{t('pricing.subtitle')}</p>

        {isAuthenticated && user?.plan && (
          <p className="mb-8 text-sm text-gray-500">
            {t('pricing.yourCurrentPlan')}: <span className="font-semibold text-emerald-600 capitalize">{user.plan.replace('_', ' ')}</span>
          </p>
        )}

        {/* Billing Toggle */}
        <div className="inline-flex items-center bg-white rounded-full p-1 shadow-md">
          <button
            onClick={() => setBillingInterval('monthly')}
            className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${
              billingInterval === 'monthly'
                ? 'bg-emerald-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('pricing.monthly')}
          </button>
          <button
            onClick={() => setBillingInterval('yearly')}
            className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${
              billingInterval === 'yearly'
                ? 'bg-emerald-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('pricing.yearly')}
            <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
              {t('pricing.save')} 17%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        {renderPlanCard('free')}
        {renderPlanCard('basic', true)}
        {renderPlanCard('pro')}
      </div>

      {/* FAQ or Additional Info */}
      <div className="max-w-4xl mx-auto mt-16 text-center">
        <p className="text-gray-600">
          {t('pricing.allPlansInclude')}: {t('pricing.securePayments')}, {t('pricing.cancelAnytime')}
        </p>
      </div>
    </div>
  );
}

export default Pricing;
